/**
 * UI chrome strings for the widget (labels, buttons, notices). The AI reply
 * itself is written by the language layer in whatever language the visitor
 * uses; the chrome falls back to English for languages not listed here.
 */
export type UiLang = "en" | "de" | "fr" | "tr";

export const UI_LANGS: UiLang[] = ["en", "de", "fr", "tr"];

export function uiLang(tag: string | null | undefined): UiLang {
  const base = (tag ?? "en").toLowerCase().split(/[-_]/)[0];
  return (UI_LANGS as string[]).includes(base) ? (base as UiLang) : "en";
}

type Strings = {
  brand: string;
  tagline: string;
  placeholder: string;
  send: string;
  close: string;
  open: string;
  new_search: string;
  interpreting: string;
  searching: string;
  checking_availability: string;
  results: string;
  partial_results: string;
  relax_title: string;
  remove: string;
  accept_unknown: string;
  change_dates: string;
  derived: string;
  derived_title: string;
  error_rate_limited: string;
  error_timeout: string;
  limit_notice: string;
  remaining: string;
  exhausted: string;
  contact_whatsapp: string;
  contact_team: string;
  view_villa: string;
  guests: string;
  bedrooms: string;
  bathrooms: string;
  available: string;
  unavailable: string;
  not_verified: string;
  price_from_night: string;
  price_total: string;
  price_on_page: string;
  not_confirmed: string;
  notice_demo: string;
  notice_index_partial: string;
  notice_prices_from: string;
  notice_unverified: string;
  notice_partially_checked: string;
  notice_budget_dates: string;
  notice_unsupported: string;
  notice_llm_error: string;
  notice_currency_unavailable: string;
  error: string;
  footer: string;
  criteria: Record<string, string>;
};

const criteria_en = {
  destination: "Destination",
  dates: "Dates",
  guests: "Guests",
  bedrooms: "Bedrooms",
  bathrooms: "Bathrooms",
  budget: "Budget",
  centre_distance: "Distance to centre",
  beach_distance: "Distance to beach",
  sea_view: "Sea view",
  sight_protected: "Sight-protected pool (conservative villa)",
  private_pool: "Private pool",
  heated_pool: "Heated pool",
  indoor_pool: "Indoor pool",
  children_pool: "Children's pool",
  jacuzzi: "Jacuzzi",
  sauna: "Sauna",
  hammam: "Hammam",
  gym: "Gym",
  near_sea: "Near the sea",
  in_nature: "In nature",
  child_friendly: "Child friendly",
  honeymoon: "Honeymoon",
  luxury: "Luxury",
  air_conditioning: "Air conditioning",
  wifi: "Wi-Fi",
  barbecue: "Barbecue",
  car_park: "Car park",
  garden: "Garden",
  terrace: "Terrace",
  game_room: "Game room",
};

export const STRINGS: Record<UiLang, Strings> = {
  en: {
    brand: "Villamine AI",
    tagline: "Describe your perfect villa…",
    placeholder: "Describe your perfect villa…",
    send: "Search",
    close: "Close",
    open: "Open Villamine AI",
    new_search: "New search",
    interpreting: "Understanding your request",
    searching: "Searching Villamine",
    checking_availability: "Checking availability with Villamine",
    results: "Matching villas",
    partial_results: "Close matches, not fully confirmed",
    relax_title: "No exact match. Relax a requirement:",
    remove: "Remove {c}",
    accept_unknown: "Accept unconfirmed {c}",
    change_dates: "Change dates",
    derived: "based on listing info",
    derived_title: "Not stated directly by Villamine; derived from related information in the listing.",
    error_rate_limited: "The AI assistant is temporarily unavailable. Please try again in a few minutes or contact our team.",
    limit_notice: "A maximum of {n} messages is available for your villa search. Please phrase your wishes and questions as precisely as possible so that our AI agent can help you in the best possible way.",
    remaining: "{n} messages available",
    exhausted: "Thank you for your enquiry. Our team will be glad to assist you personally with anything further.",
    contact_whatsapp: "Contact our team on WhatsApp",
    contact_team: "Contact our team",
    error_timeout: "Villamine AI took too long to answer. Please try again.",
    view_villa: "View villa",
    guests: "guests",
    bedrooms: "bedrooms",
    bathrooms: "baths",
    available: "Available",
    unavailable: "Not available",
    not_verified: "Availability not verified",
    price_from_night: "from {amount} / night",
    price_total: "{amount} for {nights} nights",
    price_on_page: "Price on villa page",
    not_confirmed: "not confirmed",
    notice_demo: "Demo interpreter active: no AI credentials configured. Villa data is real Villamine data.",
    notice_index_partial: "Searching {n} of {total} Villamine villas.",
    notice_prices_from: "“From” prices are Villamine's lowest nightly rate; the villa page shows the price for your dates.",
    notice_unverified: "Availability could not be verified with Villamine right now.",
    notice_partially_checked: "Availability verified for the villas shown.",
    notice_budget_dates: "Add your dates so the budget can be applied.",
    notice_unsupported: "Some requirements cannot be filtered with Villamine data and were not applied.",
    notice_llm_error: "AI interpretation temporarily unavailable; a basic parser was used.",
    notice_currency_unavailable: "Villamine's exchange rate for your currency is not available right now; prices are shown in Turkish lira.",
    error: "Villamine AI is temporarily unavailable.",
    footer: "Searches Villamine's verified villa data",
    criteria: criteria_en,
  },
  de: {
    brand: "Villamine AI",
    tagline: "Beschreiben Sie Ihre perfekte Villa…",
    placeholder: "Beschreiben Sie Ihre perfekte Villa…",
    send: "Suchen",
    close: "Schließen",
    open: "Villamine AI öffnen",
    new_search: "Neue Suche",
    interpreting: "Anfrage wird verstanden",
    searching: "Villamine wird durchsucht",
    checking_availability: "Verfügbarkeit wird bei Villamine geprüft",
    results: "Passende Villen",
    partial_results: "Nahe Treffer, nicht vollständig bestätigt",
    relax_title: "Keine exakte Übereinstimmung. Anforderung lockern:",
    remove: "{c} entfernen",
    accept_unknown: "{c} unbestätigt akzeptieren",
    change_dates: "Reisedaten ändern",
    derived: "laut Eintrag abgeleitet",
    derived_title: "Von Villamine nicht direkt angegeben; aus verwandten Angaben im Eintrag abgeleitet.",
    error_rate_limited: "Der KI-Assistent ist vorübergehend nicht verfügbar. Bitte versuchen Sie es in einigen Minuten erneut oder kontaktieren Sie unser Team.",
    limit_notice: "Für Ihre Villensuche stehen Ihnen maximal {n} Nachrichten zur Verfügung. Bitte formulieren Sie Ihre Wünsche und Fragen möglichst präzise, damit unser KI-Agent Ihnen bestmöglich helfen kann.",
    remaining: "{n} Nachrichten verfügbar",
    exhausted: "Vielen Dank für Ihre Anfrage. Für weitere Unterstützung steht Ihnen unser Team gerne persönlich zur Verfügung.",
    contact_whatsapp: "Unser Team per WhatsApp kontaktieren",
    contact_team: "Unser Team kontaktieren",
    error_timeout: "Villamine AI hat zu lange gebraucht. Bitte versuchen Sie es erneut.",
    view_villa: "Villa ansehen",
    guests: "Gäste",
    bedrooms: "Schlafzimmer",
    bathrooms: "Bäder",
    available: "Verfügbar",
    unavailable: "Nicht verfügbar",
    not_verified: "Verfügbarkeit nicht geprüft",
    price_from_night: "ab {amount} / Nacht",
    price_total: "{amount} für {nights} Nächte",
    price_on_page: "Preis auf der Villenseite",
    not_confirmed: "nicht bestätigt",
    notice_demo: "Demo-Interpreter aktiv: keine KI-Zugangsdaten konfiguriert. Villendaten sind echte Villamine-Daten.",
    notice_index_partial: "{n} von {total} Villamine-Villen werden durchsucht.",
    notice_prices_from: "„Ab“-Preise sind Villamines niedrigster Nachtpreis; die Villenseite zeigt den Preis für Ihre Daten.",
    notice_unverified: "Die Verfügbarkeit konnte gerade nicht bei Villamine geprüft werden.",
    notice_partially_checked: "Verfügbarkeit für die angezeigten Villen geprüft.",
    notice_budget_dates: "Geben Sie Reisedaten an, damit das Budget angewendet werden kann.",
    notice_unsupported: "Einige Anforderungen lassen sich mit Villamine-Daten nicht filtern und wurden nicht angewendet.",
    notice_llm_error: "KI-Interpretation vorübergehend nicht verfügbar; ein einfacher Parser wurde verwendet.",
    notice_currency_unavailable: "Villamines Wechselkurs für Ihre Währung ist gerade nicht verfügbar; Preise werden in türkischer Lira angezeigt.",
    error: "Villamine AI ist vorübergehend nicht verfügbar.",
    footer: "Durchsucht Villamines geprüfte Villendaten",
    criteria: {
      ...criteria_en,
      destination: "Reiseziel",
      dates: "Reisedaten",
      guests: "Gäste",
      bedrooms: "Schlafzimmer",
      bathrooms: "Bäder",
      budget: "Budget",
      centre_distance: "Entfernung zum Zentrum",
      beach_distance: "Entfernung zum Strand",
      sea_view: "Meerblick",
      sight_protected: "Sichtgeschützter Pool (Muhafazakar)",
      private_pool: "Privater Pool",
      heated_pool: "Beheizbarer Pool",
      indoor_pool: "Innenpool",
      children_pool: "Kinderbecken",
      jacuzzi: "Whirlpool",
      sauna: "Sauna",
      hammam: "Hamam",
      gym: "Fitnessraum",
      near_sea: "Meeresnähe",
      in_nature: "In der Natur",
      child_friendly: "Kinderfreundlich",
      honeymoon: "Flitterwochen",
      luxury: "Luxus",
      air_conditioning: "Klimaanlage",
      wifi: "WLAN",
      barbecue: "Grill",
      car_park: "Parkplatz",
      garden: "Garten",
      terrace: "Terrasse",
      game_room: "Spielzimmer",
    },
  },
  fr: {
    brand: "Villamine AI",
    tagline: "Décrivez votre villa idéale…",
    placeholder: "Décrivez votre villa idéale…",
    send: "Rechercher",
    close: "Fermer",
    open: "Ouvrir Villamine AI",
    new_search: "Nouvelle recherche",
    interpreting: "Compréhension de votre demande",
    searching: "Recherche chez Villamine",
    checking_availability: "Vérification des disponibilités chez Villamine",
    results: "Villas correspondantes",
    partial_results: "Résultats proches, non entièrement confirmés",
    relax_title: "Pas de correspondance exacte. Assouplir un critère :",
    remove: "Retirer {c}",
    accept_unknown: "Accepter {c} non confirmé",
    change_dates: "Changer les dates",
    derived: "déduit de l'annonce",
    derived_title: "Non indiqué directement par Villamine ; déduit d'informations connexes de l'annonce.",
    error_rate_limited: "L'assistant IA est temporairement indisponible. Veuillez réessayer dans quelques minutes ou contacter notre équipe.",
    limit_notice: "Pour votre recherche de villa, vous disposez de {n} messages au maximum. Merci de formuler vos souhaits et vos questions aussi précisément que possible, afin que notre agent IA puisse vous aider au mieux.",
    remaining: "{n} messages disponibles",
    exhausted: "Merci pour votre demande. Notre équipe se fera un plaisir de vous accompagner personnellement pour la suite.",
    contact_whatsapp: "Contacter notre équipe sur WhatsApp",
    contact_team: "Contacter notre équipe",
    error_timeout: "Villamine AI a mis trop de temps à répondre. Veuillez réessayer.",
    view_villa: "Voir la villa",
    guests: "personnes",
    bedrooms: "chambres",
    bathrooms: "sdb",
    available: "Disponible",
    unavailable: "Indisponible",
    not_verified: "Disponibilité non vérifiée",
    price_from_night: "dès {amount} / nuit",
    price_total: "{amount} pour {nights} nuits",
    price_on_page: "Prix sur la page de la villa",
    not_confirmed: "non confirmé",
    notice_demo: "Interpréteur de démonstration actif : aucun identifiant IA configuré. Les données des villas sont de vraies données Villamine.",
    notice_index_partial: "Recherche parmi {n} des {total} villas Villamine.",
    notice_prices_from: "Les prix « dès » sont le tarif nocturne le plus bas de Villamine ; la page de la villa indique le prix pour vos dates.",
    notice_unverified: "La disponibilité n'a pas pu être vérifiée auprès de Villamine pour le moment.",
    notice_partially_checked: "Disponibilité vérifiée pour les villas affichées.",
    notice_budget_dates: "Indiquez vos dates pour appliquer le budget.",
    notice_unsupported: "Certains critères ne peuvent pas être filtrés avec les données Villamine et n'ont pas été appliqués.",
    notice_llm_error: "Interprétation IA temporairement indisponible ; un analyseur basique a été utilisé.",
    notice_currency_unavailable: "Le taux de change Villamine pour votre devise n'est pas disponible pour le moment ; les prix sont affichés en livres turques.",
    error: "Villamine AI est temporairement indisponible.",
    footer: "Recherche dans les données vérifiées de Villamine",
    criteria: {
      ...criteria_en,
      destination: "Destination",
      dates: "Dates",
      guests: "Personnes",
      bedrooms: "Chambres",
      bathrooms: "Salles de bain",
      budget: "Budget",
      centre_distance: "Distance au centre",
      beach_distance: "Distance à la plage",
      sea_view: "Vue mer",
      sight_protected: "Piscine à l'abri des regards (villa conservatrice)",
      private_pool: "Piscine privée",
      heated_pool: "Piscine chauffée",
      indoor_pool: "Piscine intérieure",
      children_pool: "Bassin enfants",
      jacuzzi: "Jacuzzi",
      sauna: "Sauna",
      hammam: "Hammam",
      gym: "Salle de sport",
      near_sea: "Près de la mer",
      in_nature: "En pleine nature",
      child_friendly: "Adapté aux enfants",
      honeymoon: "Lune de miel",
      luxury: "Luxe",
      air_conditioning: "Climatisation",
      wifi: "Wi-Fi",
      barbecue: "Barbecue",
      car_park: "Parking",
      garden: "Jardin",
      terrace: "Terrasse",
      game_room: "Salle de jeux",
    },
  },
  tr: {
    brand: "Villamine AI",
    tagline: "Hayalinizdeki villayı anlatın…",
    placeholder: "Hayalinizdeki villayı anlatın…",
    send: "Ara",
    close: "Kapat",
    open: "Villamine AI'ı aç",
    new_search: "Yeni arama",
    interpreting: "İsteğiniz anlaşılıyor",
    searching: "Villamine'de aranıyor",
    checking_availability: "Villamine ile müsaitlik kontrol ediliyor",
    results: "Uygun villalar",
    partial_results: "Yakın sonuçlar, tam doğrulanmamış",
    relax_title: "Tam eşleşme yok. Bir kriteri gevşetin:",
    remove: "{c} kaldır",
    accept_unknown: "Doğrulanmamış {c} kabul et",
    change_dates: "Tarihleri değiştir",
    derived: "ilandan çıkarım",
    derived_title: "Villamine tarafından doğrudan belirtilmemiş; ilandaki ilgili bilgilerden çıkarılmıştır.",
    error_rate_limited: "Yapay zekâ asistanı geçici olarak kullanılamıyor. Lütfen birkaç dakika sonra tekrar deneyin veya ekibimizle iletişime geçin.",
    limit_notice: "Villa aramanız için en fazla {n} mesaj hakkınız bulunmaktadır. Yapay zekâ ajanımızın size en iyi şekilde yardımcı olabilmesi için lütfen isteklerinizi ve sorularınızı mümkün olduğunca net ifade edin.",
    remaining: "{n} mesaj hakkınız kaldı",
    exhausted: "Talebiniz için teşekkür ederiz. Daha fazla destek için ekibimiz size memnuniyetle kişisel olarak yardımcı olacaktır.",
    contact_whatsapp: "Ekibimize WhatsApp'tan ulaşın",
    contact_team: "Ekibimizle iletişime geçin",
    error_timeout: "Villamine AI yanıt vermekte çok gecikti. Lütfen tekrar deneyin.",
    view_villa: "Villayı gör",
    guests: "kişi",
    bedrooms: "yatak odası",
    bathrooms: "banyo",
    available: "Müsait",
    unavailable: "Müsait değil",
    not_verified: "Müsaitlik doğrulanmadı",
    price_from_night: "{amount} / geceden itibaren",
    price_total: "{nights} gece için {amount}",
    price_on_page: "Fiyat villa sayfasında",
    not_confirmed: "doğrulanmamış",
    notice_demo: "Demo yorumlayıcı aktif: yapay zekâ erişim bilgisi yapılandırılmadı. Villa verileri gerçek Villamine verileridir.",
    notice_index_partial: "{total} Villamine villasından {n} tanesi aranıyor.",
    notice_prices_from: "“İtibaren” fiyatları Villamine'nin en düşük gecelik fiyatıdır; tarihlerinize ait fiyat villa sayfasındadır.",
    notice_unverified: "Müsaitlik şu anda Villamine ile doğrulanamadı.",
    notice_partially_checked: "Gösterilen villalar için müsaitlik doğrulandı.",
    notice_budget_dates: "Bütçenin uygulanması için tarihlerinizi ekleyin.",
    notice_unsupported: "Bazı kriterler Villamine verileriyle filtrelenemiyor ve uygulanmadı.",
    notice_llm_error: "Yapay zekâ yorumlaması geçici olarak kullanılamıyor; basit bir ayrıştırıcı kullanıldı.",
    notice_currency_unavailable: "Villamine'nin para biriminiz için döviz kuru şu anda kullanılamıyor; fiyatlar Türk lirası olarak gösteriliyor.",
    error: "Villamine AI geçici olarak kullanılamıyor.",
    footer: "Villamine'nin doğrulanmış villa verilerinde arar",
    criteria: {
      ...criteria_en,
      destination: "Bölge",
      dates: "Tarihler",
      guests: "Kişi",
      bedrooms: "Yatak odası",
      bathrooms: "Banyo",
      budget: "Bütçe",
      centre_distance: "Merkeze uzaklık",
      beach_distance: "Plaja uzaklık",
      sea_view: "Deniz manzarası",
      sight_protected: "Korunaklı / muhafazakâr havuz",
      private_pool: "Özel havuz",
      heated_pool: "Isıtmalı havuz",
      indoor_pool: "Kapalı havuz",
      children_pool: "Çocuk havuzu",
      jacuzzi: "Jakuzi",
      sauna: "Sauna",
      hammam: "Hamam",
      gym: "Spor odası",
      near_sea: "Denize yakın",
      in_nature: "Doğa içinde",
      child_friendly: "Çocuk dostu",
      honeymoon: "Balayı",
      luxury: "Lüks",
      air_conditioning: "Klima",
      wifi: "Wi-Fi",
      barbecue: "Barbekü",
      car_park: "Otopark",
      garden: "Bahçe",
      terrace: "Teras",
      game_room: "Oyun odası",
    },
  },
};

export function t(lang: UiLang, key: keyof Omit<Strings, "criteria">, vars: Record<string, string | number> = {}): string {
  const template = STRINGS[lang][key] ?? STRINGS.en[key];
  return template.replace(/\{(\w+)\}/g, (_, k) => String(vars[k] ?? ""));
}

export function criterionLabel(lang: UiLang, key: string): string {
  return STRINGS[lang].criteria[key] ?? STRINGS.en.criteria[key] ?? key.replace(/_/g, " ");
}
